import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, UserManager } from 'oidc-client-ts';
import { authService } from '../services/authService';

interface AuthContextProps {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  userManager: UserManager;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const userManager = authService.getUserManager();

  useEffect(() => {
    // Initial user check
    const checkUser = async () => {
      try {
        const currentUser = await authService.getUser();
        setUser(currentUser);
        setIsAuthenticated(currentUser != null && !currentUser.expired);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };
    checkUser();

    // Set up event listeners for user changes
    const onUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
      setIsAuthenticated(true);
      setIsLoading(false);
    };

    const onUserUnloaded = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    const onSilentRenewError = (error: Error) => {
      // Don't immediately redirect - let the app handle gracefully
      setIsAuthenticated(false);
    };

    const onUserSignedOut = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    // Register event callbacks
    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    userManager.events.addUserSignedOut(onUserSignedOut);

    // Set up silent renew
    userManager.startSilentRenew();

    // Cleanup event handlers on unmount
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      userManager.events.removeUserSignedOut(onUserSignedOut);
      userManager.stopSilentRenew();
    };
  }, [userManager]);

  const login = () => {
    setIsLoading(true);
    authService.login();
  };

  const logout = () => {
    authService.logout();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoading,
      login, 
      logout, 
      userManager: authService.getUserManager() 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};